import { useEffect, useRef } from 'react';
import { useConsentActions } from '@repo/domain-service';

import { selectStatus, useAuthStore } from '@features/auth';

import { useReservationConsentStore } from '@shared/stores/useReservationConsentStore';
import { isReservationConsentCheckFresh } from '@shared/utils/isReservationConsentCheckFresh';

/**
 * Checks the reservation consents in the background on the screens that lead
 * into the reservation flow, so `ConsentGuard` can decide without a request.
 * Runs once per mount and only for a session that already has a token - the
 * consent query is guarded. Failures are ignored: the guard asks again.
 */
export function useReservationConsentPrefetch(): void {
  const hasSession = useAuthStore((state) => selectStatus(state) !== 'unauthenticated');
  const ownerId = useAuthStore((state) => state.user?.id);
  const { getConsents } = useConsentActions();
  const prefetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!hasSession || prefetchedRef.current) return;
    prefetchedRef.current = true;

    if (isReservationConsentCheckFresh(useReservationConsentStore.getState().check, ownerId, Date.now())) return;

    getConsents({ context: 'reservation' })
      .then((consents) => useReservationConsentStore.getState().saveCheck(ownerId, consents))
      .catch(() => {});
  }, [hasSession, ownerId, getConsents]);
}
